import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Compact "Jobs Market Pulse" widget — top 5 headlines, region-tagged.
 * Rendered near the top of HomePage. Zero-cost data (Google News RSS).
 */
export default function NewsPulse() {
  const [items, setItems] = useState([]);
  const REGION_ORDER = ["India", "US", "UK", "Singapore", "China"];

  useEffect(() => {
    let alive = true;
    api
      .get("/news/latest", { params: { limit: 20 } })
      .then((r) => {
        if (!alive) return;
        // Guarantee exactly ONE headline per region (India/US/UK/Singapore/China),
        // most-recent-first within each region — never let one region dominate.
        const all = r.data.items || [];
        const oneOfEach = REGION_ORDER
          .map((region) => all.find((n) => n.region === region))
          .filter(Boolean);
        setItems(oneOfEach);
      })
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, []);

  if (!items.length) return null;

  return (
    <section
      className="border-t border-b px-6 py-10"
      style={{ borderColor: "var(--karm-paper-edge)" }}
      aria-label="Jobs Market Pulse — latest hiring news"
      data-testid="home-news-pulse"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <div>
            <p className="small-caps mb-1">— Jobs Market Pulse —</p>
            <h2
              className="font-serif italic text-2xl md:text-3xl tracking-tight"
              style={{ color: "var(--karm-ink)" }}
            >
              Hiring signals from India, US, UK, Singapore & China.
            </h2>
          </div>
          <Link
            to="/news"
            className="small-caps hover:underline"
            data-testid="home-news-pulse-more"
            style={{ color: "var(--karm-vermillion)" }}
          >
            All news →
          </Link>
        </div>
        <ol className="grid md:grid-cols-2 gap-x-8 gap-y-4">
          {items.slice(0, 5).map((n, i) => (
            <li key={n.link || i} data-testid={`home-news-item-${i}`}>
              <a
                href={n.link}
                target="_blank"
                rel="noopener noreferrer"
                className="group block py-2"
              >
                <div
                  className="text-[10px] uppercase tracking-widest mb-1"
                  style={{ color: "var(--karm-vermillion)" }}
                >
                  {n.region} · <span style={{ color: "var(--karm-ink-muted)" }}>{n.source}</span>
                </div>
                <div
                  className="font-serif text-base leading-snug group-hover:underline"
                  style={{ color: "var(--karm-ink)" }}
                >
                  {n.title}
                  <ArrowUpRight
                    className="inline-block ml-1 h-3.5 w-3.5 opacity-60 group-hover:opacity-100"
                    style={{ verticalAlign: "middle" }}
                  />
                </div>
              </a>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
